import React, { useEffect, useRef } from 'react';
import { useLoaderData, useParams } from 'react-router-dom';
import {
  VaBreadcrumbs,
  VaLoadingIndicator,
} from '@department-of-veterans-affairs/component-library/dist/react-bindings';
import CombinedHistory from '../components/CombinedHistory';
import SortForm from '../components/SortForm';
import Pagination from '../components/Pagination';
import PaginationMeta from '../components/PaginationMeta';
import SubmissionsPageResults from '../components/SubmissionsPageResults';
import ClaimantDetailsWrapper from '../components/ClaimantDetailsWrapper';
import ClaimantNotFound from '../components/ClaimantNotFound';
import { claimantSubmissionHistoryBC } from '../utilities/poaRequests';
import api from '../utilities/api';
import { loadClaimantSubmissionHistory } from '../utilities/combinedHistory';
import store from '../utilities/store';
import { useHistoryReload } from '../utilities/useHistoryReload';
import { SUBMISSION_DEFAULTS, SORT_DEFAULTS } from '../utilities/constants';

const NO_NAME = Object.freeze({ firstName: '', lastName: '' });

/**
 * The claimant's name as the loader last gave it for this claimant.
 * Refresh reruns the whole loader, and a failed name request leaves the
 * name empty, so without this a name already on screen would turn into
 * dashes while the history reloaded fine. Another claimant's page starts
 * from what its own loader gives.
 *
 * @param {{ firstName: string, lastName: string }|undefined} claimant The
 *   loader's name, or undefined when the loader gave none.
 * @param {string} claimantId The route's claimant.
 * @returns {{ firstName: string, lastName: string }} The name to show.
 */
const useLastKnownClaimant = (claimant = NO_NAME, claimantId) => {
  const known = useRef({ claimantId, claimant });
  const hasName = Boolean(claimant.firstName || claimant.lastName);

  useEffect(() => {
    if (hasName) known.current = { claimantId, claimant };
  }, [claimant, claimantId, hasName]);

  if (hasName || known.current.claimantId !== claimantId) return claimant;
  return known.current.claimant;
};

/**
 * One claimant's submission history. With the combined history on, a failed
 * name request leaves the name empty and keeps the history, and a 403 shows
 * "Claimant not found" instead. A name request that fails on Refresh keeps
 * the name already shown.
 *
 * @returns {JSX.Element}
 */
const ClaimantSubmissionHistoryPage = () => {
  const loaderData = useLoaderData();
  const { claimantId } = useParams();
  const { combinedHistory, abortCombinedHistory } = loaderData;
  const { history, isLoading, onRefresh } = useHistoryReload(
    combinedHistory,
    claimantId,
    abortCombinedHistory,
  );
  const claimant = useLastKnownClaimant(loaderData.claimant, claimantId);
  if (loaderData.notRepresented) return <ClaimantNotFound />;
  const submissions = loaderData.data || [];
  const meta = loaderData.meta || {};
  // The header shows "—" for a missing name part, as the claimant overview
  // does.
  const nameOrDash = name => (combinedHistory ? name || '—' : name);
  const firstName = nameOrDash(claimant.firstName);
  const lastName = nameOrDash(claimant.lastName);
  // The tab title uses the raw names, so a missing part leaves no dash.
  const titleName = [claimant.lastName, claimant.firstName]
    .filter(Boolean)
    .join(', ');
  const sortOptions = [
    {
      sort: 'newest',
      label: 'Newest',
      statusLabel: 'Submitted date (newest)',
    },
    {
      sort: 'oldest',
      label: 'Oldest',
      statusLabel: 'Submitted date (oldest)',
    },
  ];

  document.title = `${
    titleName ? `Claimant ${titleName}'s` : 'Claimant'
  } submission history | Accredited Representative Portal | Veterans Affairs`;
  return (
    <section
      className={`vads-u-width--full claimant-details-submission-history${
        combinedHistory ? ' submission-history-page' : ''
      }`}
    >
      <div className="claimant-details-submission-history__breadcrumbs">
        <VaBreadcrumbs
          breadcrumbList={claimantSubmissionHistoryBC}
          homeVeteransAffairs={false}
          wrapping
        />
      </div>

      <ClaimantDetailsWrapper firstName={firstName} lastName={lastName}>
        <h2
          data-testid="submission-history-page-heading"
          className="vads-u-margin-y--0"
        >
          Submission history
        </h2>
        {combinedHistory ? (
          <>
            <p className="va-introtext">
              Review submissions for the claimant in the past 60 days.
            </p>
            <CombinedHistory
              submissions={history?.submissions}
              hasError={history?.error}
              isLoading={isLoading}
              partial={history?.partial}
              onRefresh={onRefresh}
              claimantId={claimantId}
              claimantName={titleName}
              loadStartedAt={history?.loadStartedAt}
              scope="claimant"
            />
          </>
        ) : (
          <>
            <p className="va-introtext">
              Review online forms submitted for the claimant in the past 60
              days.
            </p>
            {submissions.length ? (
              <SortForm
                className="vads-u-margin-top--2"
                options={sortOptions}
                defaults={SORT_DEFAULTS}
              />
            ) : (
              ''
            )}
            {meta && submissions.length ? (
              <PaginationMeta
                meta={meta}
                results={submissions}
                resultType="submissions"
                defaults={SORT_DEFAULTS}
                sortOptions={sortOptions}
              />
            ) : (
              ''
            )}
            <div className="submissions-page-table-container">
              {isLoading ? (
                <VaLoadingIndicator message="Loading..." />
              ) : (
                <SubmissionsPageResults
                  submissions={submissions}
                  context="ClaimantSubmissionHistoryPage"
                />
              )}
            </div>
            <Pagination meta={meta} defaults={SUBMISSION_DEFAULTS} />
          </>
        )}
      </ClaimantDetailsWrapper>
    </section>
  );
};

ClaimantSubmissionHistoryPage.loader = loadClaimantSubmissionHistory(
  api,
  store,
);

export default ClaimantSubmissionHistoryPage;
